import React from 'react';

export function Intro() {
  return (
    <section className="humanizer-intro" style={{ marginTop: 24 }}>
      <h2 className="text-2xl font-semibold mb-3">What the OneAI Humanizer Does</h2>
      <p style={{ color: '#374151', lineHeight: 1.6 }}>
        The OneAI Humanizer helps you turn AI-generated drafts into clear, engaging, and human-like writing. It focuses on
        tone, rhythm, and readability so your content connects with real people — not just search engines. Paste your draft,
        choose the tone and style controls, and get back a version that sounds natural, preserves key terms, and is easy to
        read. Whether you’re polishing blog posts, customer support replies, product descriptions or social copy, the
        Humanizer speeds up editing while keeping your brand voice intact. It’s built for teams that want fast drafts without
        sacrificing authenticity: think fewer rewrites, better engagement, and content that earns trust.
      </p>
    </section>
  );
}

export function KeyFeatures() {
  return (
    <section className="humanizer-features" style={{ marginTop: 28 }}>
      <h3 className="text-xl font-semibold mb-2">Key Features</h3>
      <ul style={{ color: '#374151', lineHeight: 1.6 }}>
        <li><strong>Tone controls:</strong> Shift between professional, friendly, or casual voices with a single click.</li>
        <li><strong>Keyword preservation:</strong> Protect SEO-critical phrases so they’re not lost during editing.</li>
        <li><strong>Context-aware edits:</strong> The tool keeps meaning intact while improving flow and clarity.</li>
        <li><strong>FAQ &amp; snippet support:</strong> Generate short excerpts, meta descriptions, and answers fast.</li>
        <li><strong>Human-in-the-loop:</strong> Keep final editorial control—accept suggestions, tweak language, and publish.</li>
      </ul>
    </section>
  );
}

export function Benefits() {
  return (
    <section className="humanizer-benefits" style={{ marginTop: 28 }}>
      <h3 className="text-xl font-semibold mb-2">Benefits for Your Team</h3>
      <p style={{ color: '#374151', lineHeight: 1.6 }}>
        Using the Humanizer reduces time spent on edits and improves the emotional resonance of your content. Teams see
        faster iteration cycles because AI provides structure and the Humanizer adds personality, which shortens review
        loops and increases throughput. Better-sounding content builds trust with readers, increases time-on-page, and
        typically improves click-through and conversion rates. Because important keywords are preserved, you don’t trade
        search relevance for readability—the tool helps both at once. In short: less manual rework, more human connection,
        and clearer business results.
      </p>
    </section>
  );
}

export function UsageSteps() {
  return (
    <section className="humanizer-steps" style={{ marginTop: 28 }}>
      <h3 className="text-xl font-semibold mb-2">How to Use the Humanizer (Step-by-step)</h3>
      <ol style={{ color: '#374151', lineHeight: 1.6 }}>
        <li><strong>1. Paste your draft:</strong> Paste the AI-generated text into the input area and scan for protected phrases.</li>
        <li><strong>2. Choose tone &amp; level:</strong> Select the target tone (friendly, professional, casual) and intensity of edits.</li>
        <li><strong>3. Protect keywords:</strong> Mark any terms you need preserved for SEO or legal reasons.</li>
        <li><strong>4. Humanize:</strong> Click the Humanize button to generate a refined draft that keeps meaning and improves flow.</li>
        <li><strong>5. Review &amp; publish:</strong> Tweak the suggestions, run a quick QA, and copy the final text into your CMS.</li>
      </ol>
    </section>
  );
}

export function Faq() {
  return (
    <section className="humanizer-faq" style={{ marginTop: 28 }}>
      <h3 className="text-xl font-semibold mb-2">Frequently Asked Questions</h3>
      <div style={{ color: '#374151', lineHeight: 1.6 }}>
        <h4 className="font-semibold">1. Will the Humanizer change my keywords?</h4>
        <p>By default the tool suggests phrasing changes around your keywords but will not remove or alter protected terms when you mark them. Use the keyword protection feature to guarantee exact matches remain.</p>

        <h4 className="font-semibold">2. Is the output safe to publish without human review?</h4>
        <p>The Humanizer improves tone and clarity but we recommend a final human review for facts, brand-specific language, and compliance-sensitive content. Think of the tool as a powerful assistant, not a replacement for editorial judgment.</p>

        <h4 className="font-semibold">3. Can I control the degree of change?</h4>
        <p>Yes — you can choose light, moderate, or aggressive editing levels. Light edits focus on rhythm and clarity, while aggressive edits rewrite sentences more extensively to sound natural and vivid.</p>

        <h4 className="font-semibold">4. How does it help SEO?</h4>
        <p>The Humanizer increases readability and engagement, which can improve behavioral SEO signals like time-on-page and CTR. It also preserves targeted keywords when configured, so you don’t lose search relevance while improving copy quality.</p>

        <h4 className="font-semibold">5. Can it handle technical or legal text?</h4>
        <p>Yes — the tool can process technical and regulated content but we recommend setting protected terms and performing a careful human review to ensure accuracy and compliance.</p>

        <h4 className="font-semibold">6. What formats does it support?</h4>
        <p>The Humanizer works well for blog posts, product descriptions, email copy, support replies, and short-form social content. For long-form technical documents, use the tool iteratively section-by-section and review for factual precision.</p>
      </div>
    </section>
  );
}

export default function HumanizerSeoContent() {
  return (
    <div className="humanizer-seo-content" style={{ marginTop: 32 }}>
      <Intro />
      <KeyFeatures />
      <Benefits />
      <UsageSteps />
      <Faq />
    </div>
  );
}
